import { createContext, useContext, type ReactNode } from 'react'

import { CookieBanner } from '@/components/widgets/CookieBanner'
import { useAuth } from '@/context/AuthContext'
import { useCookieConsentBanner } from '@/hooks/useCookieConsentBanner'
import { initAmplitudeIfAllowed, resetUser, setAnalyticsOptOut } from '@/lib/analytics'
import type { ConsentCategories } from '@/lib/cookieConsent'

interface CookieConsentContextValue {
  openBanner: () => void
}

const CookieConsentContext = createContext<CookieConsentContextValue | undefined>(undefined)

// Granting Analytics starts Amplitude; revoking stops it and severs the identity.
function applyAnalyticsTransition(previous: ConsentCategories | null, next: ConsentCategories) {
  const wasGranted = previous?.analytics === true

  if (!wasGranted && next.analytics) {
    initAmplitudeIfAllowed()
    setAnalyticsOptOut(false)
  } else if (wasGranted && !next.analytics) {
    setAnalyticsOptOut(true)
    resetUser()
  }
}

// SPA glue: mounts the banner and supplies the auth gate + analytics transition the shared hook
// does not know about. /mcp-connect uses the same hook and banner without this provider.
export function CookieConsentProvider({ children }: { children: ReactNode }) {
  // A 401 from /me hard-redirects the page; opening before auth settles paints the banner twice.
  const { loading: authLoading } = useAuth()
  const banner = useCookieConsentBanner({ autoOpenReady: !authLoading, onCommit: applyAnalyticsTransition })

  const value: CookieConsentContextValue = {
    openBanner: () => banner.setOpen(true),
  }

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
      <CookieBanner {...banner} />
    </CookieConsentContext.Provider>
  )
}

export function useCookieConsent(): CookieConsentContextValue {
  const ctx = useContext(CookieConsentContext)
  if (!ctx) throw new Error('useCookieConsent must be used within CookieConsentProvider')
  return ctx
}
